import * as React from 'react';
import { Stack, Typography } from '@mui/material';
import { Building as BuildingIcon } from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';

import type { UnitType } from '@/types/v2/unit-type';
import axios from '@/lib/v2/axios';
import ProfessionalDialog, { DialogActionButtons } from '@/components/common/professional-dialog';

import { CreateOrEditUnitForm } from './create-or-edit-unit-form';
import type { Unit } from '@/types/v2/unit';

interface CreateOrEditUnitModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (unit: Unit) => void;
  mode: 'create' | 'edit';
  initialData?: {}; // Change from Community | {} to just Community
  unitId?: string;
  communityUnitTypes?: UnitType[];
  communityId?: string | number;
}

export function CreateOrEditUnitModal({
  open,
  onClose,
  onSuccess,
  mode,
  initialData,
  unitId,
  communityUnitTypes,
  communityId,
}: CreateOrEditUnitModalProps): React.JSX.Element {
  const { data } = useQuery({
    // Use local UnitType
    queryKey: [`${communityId}-unit-types`],
    queryFn: async () => {
      const response = await axios.get('/api/v2/unit-types');
      return response.data;
    },
  });

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSuccess = React.useCallback(
    (unit: Unit) => {
      onSuccess(unit);
    },
    [onSuccess, handleClose]
  );

  return (
    <ProfessionalDialog
      animation="slide"
      autoHeight
      fullWidth={false}
      icon={<BuildingIcon size={24} />}
      maxWidth="sm"
      onClose={onClose}
      open={open}
      subtitle={
        mode === 'create'
          ? 'Creating a unit helps organize tenants, streamlining maintenance request tracking.'
          : 'Editing a unit allows for adjustments to tenant organization, enhancing maintenance request management.'
      }
      title={mode === 'create' ? 'Create Unit' : 'Edit Unit'}
      variant="professional"
    >
      <CreateOrEditUnitForm
        communityId={communityId}
        communityUnitTypes={communityUnitTypes}
        initialData={initialData as Unit}
        mode={mode}
        onSuccess={handleSuccess}
        unitId={unitId}
        unitTypes={data?.unitTypes as UnitType[]}
      />
    </ProfessionalDialog>
  );
}
